import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CalendarBlank,
  CaretDown,
  DownloadSimple,
  Warning,
} from "@phosphor-icons/react";

// Public report viewer — no login required. The JWT in the path grants
// read-only access scoped to report:public.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/reports`;

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function PublicReport() {
  const { token } = useParams();
  const today = new Date().toISOString().slice(0, 10);
  const thirty = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);

  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("amazon");
  const [start, setStart] = useState(thirty);
  const [end, setEnd] = useState(today);
  const [loading, setLoading] = useState(false);

  const [amazon, setAmazon] = useState(null);
  const [impact, setImpact] = useState(null);
  const [openDate, setOpenDate] = useState(null);
  // Publisher filter — "all" or a publisher_sub_id. Applies to both tabs
  // so the same dropdown drives Amazon and Impact views.
  const [pubFilter, setPubFilter] = useState("all");

  // Verify the token and grab the banner label once.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/meta`, { params: { token } });
        setMeta(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "This report link is invalid or expired.");
      }
    })();
  }, [token]);

  // Load data for the active tab whenever tab/window changes.
  useEffect(() => {
    if (!meta) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (tab === "amazon") {
          const { data } = await axios.get(`${API_BASE}/amazon`, {
            params: { token, start_date: start, end_date: end },
          });
          if (!cancelled) setAmazon(data);
        } else {
          const { data } = await axios.get(`${API_BASE}/impact`, {
            params: { token, start_date: start, end_date: end },
          });
          if (!cancelled) setImpact(data);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, tab, start, end, meta]);

  const amzTotals = amazon?.totals || { our_clicks: 0, feed_clicks: 0, feed_revenue: 0, pub_earnings: 0, margin: 0 };
  const impTotals = impact?.totals || { actions: 0, approved: 0, pending: 0, reversed: 0, payout_approved: 0, payout_total: 0 };

  // ─── Publisher filter ───────────────────────────────────────────────
  // Build dropdown options from the union of pubs across Amazon + Impact
  // data in the current window. We key on publisher_sub_id so the same
  // pub on both networks shows up once.
  const pubOptions = useMemo(() => {
    const m = new Map();
    const addRow = (r) => {
      const k = r.publisher_sub_id || r.publisher_email || "";
      if (!k) return;
      if (!m.has(k)) {
        m.set(k, {
          key: k,
          label: r.publisher_company || r.publisher_email || k,
          sub_id: r.publisher_sub_id || "",
        });
      }
    };
    (amazon?.data || []).forEach(addRow);
    (impact?.data || []).forEach(addRow);
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [amazon, impact]);

  const matchesPub = (r) => {
    if (pubFilter === "all") return true;
    return (r.publisher_sub_id || r.publisher_email || "") === pubFilter;
  };

  // Filter rows + recompute totals client-side so the dropdown is instant
  // (no server round-trip per change).
  const filteredAmazon = useMemo(() => {
    if (!amazon) return null;
    if (pubFilter === "all") return amazon;
    const data = (amazon.data || []).filter(matchesPub);
    const totals = data.reduce(
      (t, r) => {
        t.our_clicks += r.our_clicks || 0;
        t.feed_clicks += r.feed_clicks || 0;
        t.feed_revenue += r.feed_revenue || 0;
        t.pub_earnings += r.pub_earnings || 0;
        t.margin += r.margin || 0;
        return t;
      },
      { our_clicks: 0, feed_clicks: 0, feed_revenue: 0, pub_earnings: 0, margin: 0 },
    );
    return { ...amazon, data, totals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amazon, pubFilter]);

  const filteredImpact = useMemo(() => {
    if (!impact) return null;
    if (pubFilter === "all") return impact;
    const data = (impact.data || []).filter(matchesPub);
    const totals = data.reduce(
      (t, r) => {
        t.actions += r.actions || 0;
        t.approved += r.approved || 0;
        t.pending += r.pending || 0;
        t.reversed += r.reversed || 0;
        t.payout_approved += r.payout_approved || 0;
        t.payout_total += r.payout_total || 0;
        return t;
      },
      { actions: 0, approved: 0, pending: 0, reversed: 0, payout_approved: 0, payout_total: 0 },
    );
    return { ...impact, data, totals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impact, pubFilter]);

  const amzView = filteredAmazon || amazon;
  const impView = filteredImpact || impact;
  const amzT = amzView?.totals || amzTotals;
  const impT = impView?.totals || impTotals;

  // Impact pub/brand pivot for the summary strip.
  const impactByPub = useMemo(() => {
    if (!impView?.data) return [];
    const m = new Map();
    impView.data.forEach((r) => {
      const key = r.publisher_sub_id || r.publisher_email || "(none)";
      const e = m.get(key) || {
        publisher_sub_id: r.publisher_sub_id,
        publisher_company: r.publisher_company,
        publisher_email: r.publisher_email,
        actions: 0, approved: 0,
        sale_amount: 0, sale_amount_approved: 0,
        payout_approved: 0,
      };
      e.actions += r.actions;
      e.approved += r.approved;
      e.sale_amount += r.sale_amount || 0;
      e.sale_amount_approved += r.sale_amount_approved || 0;
      e.payout_approved += r.payout_approved;
      m.set(key, e);
    });
    return [...m.values()].sort((a, b) => b.sale_amount - a.sale_amount);
  }, [impView]);

  // Server already returns `by_brand`; fall back to client aggregation
  // if absent (old report links pre-2026-05-11 don't have it).
  const impactByBrand = useMemo(() => {
    if (impView?.by_brand?.length) return impView.by_brand;
    if (!impView?.data) return [];
    const m = new Map();
    impView.data.forEach((r) => {
      const e = m.get(r.brand) || {
        brand: r.brand, actions: 0, approved: 0,
        sale_amount: 0, sale_amount_approved: 0,
        payout_approved: 0,
      };
      e.actions += r.actions;
      e.approved += r.approved;
      e.sale_amount += r.sale_amount || 0;
      e.sale_amount_approved += r.sale_amount_approved || 0;
      e.payout_approved += r.payout_approved;
      m.set(r.brand, e);
    });
    return [...m.values()].sort((a, b) => b.sale_amount - a.sale_amount);
  }, [impView]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Warning size={32} weight="fill" className="text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Can't open this report</h1>
          <p className="text-sm text-slate-600">{err}</p>
          <p className="text-xs text-slate-400 mt-4">Ask admin to re-send a fresh link.</p>
        </div>
      </div>
    );
  }

  if (!meta) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500">Loading report…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-report-page">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-400 mb-1">RedClub report</div>
            <h1 className="text-2xl font-bold text-slate-900">{meta.label}</h1>
            <p className="text-[11px] text-slate-500 mt-1">
              Read-only · link expires {new Date(meta.expires_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Select value={pubFilter} onValueChange={setPubFilter}>
              <SelectTrigger className="h-9 w-[200px] bg-white" data-testid="pr-pub-filter">
                <SelectValue placeholder="All publishers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All publishers</SelectItem>
                {pubOptions.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                    {p.sub_id ? <span className="text-slate-400 font-mono ml-2">{p.sub_id}</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2">
              <CalendarBlank size={14} className="text-slate-400" />
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} max={end} className="h-7 text-xs w-36 border-0 p-0 focus-visible:ring-0" data-testid="pr-start" />
              <span className="text-slate-300">→</span>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} min={start} max={today} className="h-7 text-xs w-36 border-0 p-0 focus-visible:ring-0" data-testid="pr-end" />
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="amazon" data-testid="pr-tab-amazon">Amazon (Shopnomix)</TabsTrigger>
            <TabsTrigger value="impact" data-testid="pr-tab-impact">Impact & other CPA</TabsTrigger>
          </TabsList>

          {/* AMAZON */}
          <TabsContent value="amazon">
            <KPIGrid
              items={[
                { label: "Our clicks", value: amzT.our_clicks },
                { label: "Feed clicks", value: amzT.feed_clicks },
                { label: "Feed revenue", value: `$${Number(amzT.feed_revenue).toFixed(2)}` },
                { label: "Pub earnings", value: `$${Number(amzT.pub_earnings).toFixed(2)}`, accent: "emerald" },
                { label: "Margin", value: `$${Number(amzT.margin).toFixed(2)}`, accent: "blue" },
              ]}
            />

            <div className="flex items-center justify-between mb-3 mt-6">
              <p className="text-[11px] text-slate-500">
                {new Set((amzView?.data || []).map((r) => r.date)).size} day(s) · {start} → {end}
                {pubFilter !== "all" && <span className="ml-2 text-[#002FA7]">· filtered to 1 publisher</span>}
              </p>
              <Button size="sm" variant="outline" onClick={() => _exportAmazonCsv(amzView, start, end)} disabled={!amzView?.data?.length} data-testid="pr-amazon-csv">
                <DownloadSimple size={14} className="mr-1.5" /> CSV
              </Button>
            </div>

            <div className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid="pr-amazon-table">
              {(() => {
                // Group by date. Each date collapses → expand to show pubs.
                const byDate = new Map();
                (amzView?.data || []).forEach((r) => {
                  const g = byDate.get(r.date) || {
                    date: r.date,
                    rows: [],
                    our_clicks: 0, feed_clicks: 0,
                    feed_revenue: 0, pub_earnings: 0, margin: 0,
                  };
                  g.rows.push(r);
                  g.our_clicks += r.our_clicks;
                  g.feed_clicks += r.feed_clicks;
                  g.feed_revenue += r.feed_revenue;
                  g.pub_earnings += r.pub_earnings;
                  g.margin += r.margin;
                  byDate.set(r.date, g);
                });
                const days = [...byDate.values()].sort((a, b) => b.date.localeCompare(a.date));
                if (!loading && days.length === 0) {
                  return <div className="px-4 py-8 text-center text-sm text-slate-400">No Amazon data in this window.</div>;
                }
                return days.map((d) => {
                  const isOpen = openDate === `amz:${d.date}`;
                  return (
                    <div key={d.date} className="border-b border-slate-100 last:border-0">
                      <button
                        type="button"
                        onClick={() => setOpenDate(isOpen ? null : `amz:${d.date}`)}
                        className={`w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-slate-50 transition-colors ${isOpen ? "bg-slate-50" : ""}`}
                        data-testid={`pr-amz-day-${d.date}`}
                      >
                        <CaretDown size={12} weight="bold" className={`shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        <div className="font-mono text-sm font-bold text-slate-900 w-32">{d.date}</div>
                        <div className="text-xs text-slate-500 flex-1">
                          {d.rows.length} pub{d.rows.length === 1 ? "" : "s"} · <span className="font-mono">{d.our_clicks}</span> our clicks · <span className="font-mono">{d.feed_clicks}</span> feed clicks
                        </div>
                        <div className="text-xs text-right">
                          <span className="text-slate-500">rev </span>
                          <span className="font-mono font-bold text-slate-900">${d.feed_revenue.toFixed(2)}</span>
                        </div>
                        <div className="text-xs text-right">
                          <span className="text-slate-500">pub </span>
                          <span className="font-mono font-bold text-emerald-700">${d.pub_earnings.toFixed(2)}</span>
                        </div>
                        <div className="text-xs text-right w-24">
                          <span className="text-slate-500">margin </span>
                          <span className="font-mono font-bold text-[#002FA7]">${d.margin.toFixed(2)}</span>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="bg-slate-50/60 border-t border-slate-100 px-4 py-3" data-testid={`pr-amz-day-${d.date}-expanded`}>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-200">
                                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Publisher</th>
                                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Campaign</th>
                                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Our clicks</th>
                                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Feed clicks</th>
                                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Feed EPC</th>
                                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Feed rev</th>
                                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Pub paid</th>
                                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Margin</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.rows.map((r, i) => (
                                <tr key={`${r.campaign_label}-${r.publisher_sub_id || i}`} className="border-b border-slate-100 last:border-0">
                                  <td className="px-3 py-2">
                                    <div className="text-sm text-slate-900">{r.publisher_company || r.publisher_email || <span className="text-slate-400">—</span>}</div>
                                    <div className="text-[10px] font-mono text-slate-400">{r.publisher_sub_id}</div>
                                  </td>
                                  <td className="px-3 py-2 text-sm text-slate-700">{r.campaign_label}</td>
                                  <td className="px-3 py-2 text-right font-mono text-sm">{r.our_clicks}</td>
                                  <td className="px-3 py-2 text-right font-mono text-sm">{r.feed_clicks}</td>
                                  <td className="px-3 py-2 text-right font-mono text-sm">${Number(r.feed_epc).toFixed(4)}</td>
                                  <td className="px-3 py-2 text-right font-mono text-sm font-bold">${Number(r.feed_revenue).toFixed(2)}</td>
                                  <td className="px-3 py-2 text-right font-mono text-sm font-bold text-emerald-700">${Number(r.pub_earnings).toFixed(2)}</td>
                                  <td className="px-3 py-2 text-right font-mono text-sm font-bold text-[#002FA7]">${Number(r.margin).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </TabsContent>

          {/* IMPACT */}
          <TabsContent value="impact">
            <KPIGrid
              items={[
                { label: "Conversions", value: impT.actions },
                { label: "Approved", value: impT.approved, accent: "emerald" },
                { label: "Pending", value: impT.pending },
                { label: "Reversed", value: impT.reversed, accent: "red" },
                { label: "Approved payout", value: `$${Number(impT.payout_approved).toFixed(2)}`, accent: "blue" },
              ]}
            />

            {/* Per-publisher summary strip */}
            {impactByPub.length > 0 && (
              <div className="mt-6 mb-4">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-400 mb-2">By publisher</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {impactByPub.map((p) => (
                    <div key={p.publisher_sub_id || p.publisher_email} className="bg-white border border-slate-200 rounded-md px-3 py-2.5">
                      <div className="text-sm font-medium text-slate-900 truncate">{p.publisher_company || p.publisher_email || "(unmatched)"}</div>
                      <div className="text-[10px] font-mono text-slate-400 mb-2">{p.publisher_sub_id}</div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <div className="text-[9px] uppercase tracking-wider text-slate-400">Sales</div>
                          <div className="font-mono font-semibold text-slate-900">{p.actions}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wider text-slate-400">Sale amt</div>
                          <div className="font-mono font-semibold text-slate-900">${Number(p.sale_amount).toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wider text-slate-400">Commission</div>
                          <div className="font-mono font-bold text-emerald-700">${Number(p.payout_approved).toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-brand summary strip */}
            {impactByBrand.length > 0 && (
              <div className="mt-6 mb-4">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-400 mb-2">By brand</div>
                <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
                  <table className="w-full text-sm" data-testid="pr-by-brand-table">
                    <thead className="bg-slate-50">
                      <tr className="border-b border-slate-200">
                        {["Brand","Sales","Sale amount","Approved","Commission"].map((h, i) => (
                          <th key={h} className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {impactByBrand.slice(0, 25).map((b) => (
                        <tr key={b.brand} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-2 text-sm text-slate-900">{b.brand}</td>
                          <td className="px-4 py-2 text-right font-mono text-sm">{b.actions}</td>
                          <td className="px-4 py-2 text-right font-mono text-sm font-semibold text-slate-900">${Number(b.sale_amount).toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-mono text-sm text-emerald-700">{b.approved}</td>
                          <td className="px-4 py-2 text-right font-mono text-sm font-bold text-[#002FA7]">${Number(b.payout_approved).toFixed(2)}</td>
                        </tr>
                      ))}
                      {impactByBrand.length > 25 && (
                        <tr><td colSpan={5} className="px-4 py-1.5 text-center text-[10px] text-slate-400 bg-slate-50">… {impactByBrand.length - 25} more brand{impactByBrand.length - 25 === 1 ? "" : "s"} below (see full table)</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-3 mt-6">
              <p className="text-[11px] text-slate-500">
                {(impView?.data || []).length} row(s) · {start} → {end}
                {pubFilter !== "all" && <span className="ml-2 text-[#002FA7]">· filtered to 1 publisher</span>}
              </p>
              <Button size="sm" variant="outline" onClick={() => _exportImpactCsv(impView, start, end)} disabled={!impView?.data?.length} data-testid="pr-impact-csv">
                <DownloadSimple size={14} className="mr-1.5" /> CSV
              </Button>
            </div>

            <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
              <table className="w-full text-sm" data-testid="pr-impact-table">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    {["Date","Brand","Publisher","Actions","Sale amt","Approved","Pending","Reversed","Commission"].map((h) => (
                      <th key={h} className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold ${["Date","Brand","Publisher"].includes(h) ? "text-left" : "text-right"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(impView?.data || []).map((r, i) => (
                    <tr key={`${r.date}-${r.brand}-${r.publisher_sub_id || i}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{r.date}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-900">{r.brand}</td>
                      <td className="px-4 py-2.5">
                        <div className="text-sm text-slate-900">{r.publisher_company || r.publisher_email || <span className="text-slate-400">—</span>}</div>
                        <div className="text-[10px] font-mono text-slate-400">{r.publisher_sub_id}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-sm">{r.actions}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-sm font-semibold text-slate-900">${Number(r.sale_amount || 0).toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-sm font-bold text-emerald-700">{r.approved}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-sm text-slate-500">{r.pending}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-sm text-red-600">{r.reversed}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-sm font-bold text-[#002FA7]">${Number(r.payout_approved).toFixed(2)}</td>
                    </tr>
                  ))}
                  {!loading && (!impView?.data || impView.data.length === 0) && (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-400">No Impact conversions in this window.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        <p className="text-[10px] text-slate-400 mt-6 text-center">
          Shared by RedClub · Data is read-only · Anyone with this link can view
        </p>
      </div>
    </div>
  );
}

function KPIGrid({ items }) {
  const palette = {
    emerald: "text-emerald-700",
    blue: "text-[#002FA7]",
    red: "text-red-600",
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {items.map((it) => (
        <div key={it.label} className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-400">{it.label}</div>
          <div className={`text-xl font-mono font-bold mt-1 ${palette[it.accent] || "text-slate-900"}`}>
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function _exportAmazonCsv(report, start, end) {
  if (!report?.data?.length) return;
  const headers = ["date","campaign_label","publisher_company","publisher_email","publisher_sub_id","our_clicks","feed_clicks","feed_epc","feed_revenue","pub_earnings","margin","cap_fired","pending"];
  const rows = [
    headers.join(","),
    ...report.data.map((r) => headers.map((h) => csvEscape(r[h] ?? "")).join(",")),
    "",
    `# window,${start},${end}`,
    `# exported_at,${new Date().toISOString()}`,
  ];
  downloadCsv(`amazon_report_${start}_to_${end}.csv`, rows);
}

function _exportImpactCsv(report, start, end) {
  if (!report?.data?.length) return;
  const headers = ["date","brand","publisher_company","publisher_email","publisher_sub_id","actions","sale_amount","sale_amount_approved","approved","pending","reversed","commission","payout_total"];
  const rows = [
    headers.join(","),
    ...report.data.map((r) => headers.map((h) => csvEscape(r[h] ?? "")).join(",")),
    "",
    `# window,${start},${end}`,
    `# exported_at,${new Date().toISOString()}`,
  ];
  downloadCsv(`impact_report_${start}_to_${end}.csv`, rows);
}
